import axios from 'axios'
import { createClinicalSession, createPhoneCall } from '@/services/api/voiceApi'
import { FEATURES } from '@/constants/features'
import { POSTHOG_FLAGS } from '@/constants/posthogFlags'
import { canAccessFeature } from '@/utils/access'
import { isPostHogFlagEnabled } from '@/utils/posthogFlags'

/** @type {import('@/services/api/schema').components['schemas']['WorkTypeKindEnum']} */
export const ENGAGEMENT_KIND = 'engagement'


export const ENGAGEMENTS = {
  clinicalEngagement: {
    label: 'Clinical Engagement',
    description: 'Care session with the patient: video, phone or recorded audio',
    icon: ['fas', 'user-doctor'],
    iconClass: 'text-primary-600',
    tileClass: 'bg-primary-50',
  },
  phoneCall: {
    label: 'Care Coordination Call',
    description: 'Call the care team, DME company or other contacts about the patient',
    icon: ['fas', 'phone'],
    iconClass: 'text-green-600',
    tileClass: 'bg-green-50',
  },
}

/** @typedef {import('@/services/api/schema').components['schemas']['WorkType']} WorkType */

/**
 * Per-type engagement settings, stored in WorkType.configuration.engagement.
 * @typedef {{ hide_sections?: string[], provider_participant_role?: string, launcher?: { label: string, description?: string, icon?: string } }} EngagementConfig
 */

/**
 * @param {WorkType | null | undefined} workType
 * @returns {EngagementConfig}
 */
export function engagementConfig(workType) {
  const config = /** @type {Record<string, any> | null | undefined} */ (workType?.configuration)
  return config?.engagement || {}
}

/** @typedef {import('@/services/api/schema').components['schemas']['WorkAssignment']} WorkAssignment */

/**
 * Engagement work types (other than clinical, which has its own button) that launch from an opportunity:
 * kind=engagement with a configured launcher. metricIds are the metrics the type is assigned to
 * through WorkAssignment(metric); the button only shows on opportunities of those metrics.
 * @returns {Promise<{ workTypeId: number, label: string, description: string, icon: string[], metricIds: number[] }[]>}
 */
export async function fetchLaunchableEngagements() {
  /** @type {{ data: WorkType[] }} */
  const { data } = await axios.get('/api/worktypes/', { params: { kind: ENGAGEMENT_KIND } })
  const launchable = data.filter(workType => engagementConfig(workType).launcher)
  return Promise.all(launchable.map(async workType => {
    /** @type {{ data: WorkAssignment[] }} */
    const { data: assignments } = await axios.get('/api/workassignments/', { params: { work_type: workType.id } })
    const launcher = engagementConfig(workType).launcher
    return {
      workTypeId: workType.id,
      label: launcher.label,
      description: launcher.description || '',
      icon: ['fas', launcher.icon || 'phone'],
      metricIds: assignments.map(assignment => assignment.metric).filter(Boolean),
    }
  }))
}

/**
 * The launchable engagements offered for opportunities on the given metrics.
 * @param {{ metricIds: number[] }[]} engagements
 * @param {(number | null | undefined)[]} metricIds
 */
export function engagementsForMetrics(engagements, metricIds) {
  const metrics = new Set(metricIds.filter(Boolean))
  return engagements.filter(engagement => engagement.metricIds.some(id => metrics.has(id)))
}

export function isEngagementWorkType(workType) {
  return workType?.kind === ENGAGEMENT_KIND
}

export function canStartPhoneCall() {
  return canAccessFeature(FEATURES.COMPONENT_TELEHEALTH_PHONE)
    && isPostHogFlagEnabled(POSTHOG_FLAGS.TELEHEALTH_THREE_WAY_CALLING)
}

export function phoneCallPath(workId) {
  return `/work/phonecall/${workId}`
}

export function clinicalEngagementPath(workId) {
  return `/work/clinicalengagement/${workId}`
}

async function createAndOpen(create, pathFor) {
  const tab = window.open('', '_blank')
  try {
    const work = await create()
    const path = pathFor(work.work_id)
    if (tab) tab.location.href = path
    else window.open(path, '_blank')
    return work
  } catch (error) {
    tab?.close()
    throw error
  }
}

export function startPhoneCall({ opportunityId, taskId }) {
  if (!taskId) return Promise.reject(new Error('A task is required to start a phone call.'))
  const body = { task_id: taskId }
  if (opportunityId) body.opportunity_id = opportunityId
  return createAndOpen(() => createPhoneCall(body), phoneCallPath)
}

export function startClinicalEngagement({ opportunityId, taskId }) {
  if (!opportunityId) return Promise.reject(new Error('An opportunity is required to start a clinical engagement.'))
  return createAndOpen(() => createClinicalSession({ opportunityId, taskId }), clinicalEngagementPath)
}

/** Starts an engagement session of the given work type; it opens in the same screen as Clinical Engagement. */
export function startEngagement({ workTypeId, opportunityId, taskId }) {
  if (!opportunityId) return Promise.reject(new Error('An opportunity is required to start an engagement.'))
  return createAndOpen(() => createClinicalSession({ opportunityId, taskId, workTypeId }), clinicalEngagementPath)
}
